// =============================================================================
// Utilidades geográficas: distancia entre dos coordenadas y tarifa sugerida.
// Cálculos puros — sin side effects, fáciles de testear.
// =============================================================================

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Distancia ortodrómica entre dos puntos (km) usando la fórmula de Haversine.
 */
export function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371; // Radio de la Tierra en km
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const aa = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
  return R * c;
}

/** Distancia en metros, alias usable cuando se compara contra radios cortos. */
export function haversineMeters(a: LatLng, b: LatLng): number {
  return haversineKm(a, b) * 1000;
}

/**
 * Tarifa sugerida según km recorridos y tipo de vehículo.
 * Valores en DOP, calibrados para el mercado dominicano (similar a Uber/inDriver SD):
 *   - Tarifa base por subida
 *   - Costo por km
 *   - Mínimo garantizado para que el viaje valga la pena al chofer
 */
export type VehicleKind = 'CAR' | 'MOTORCYCLE' | 'MINIBUS';

/**
 * ⚠️ SI TOCAS ESTA TABLA, TOCA TAMBIÉN `TARIFAS_DEL_SERVIDOR` en
 * functions/src/loyalty.ts. Son la misma tabla en dos compilaciones que no
 * comparten módulos; si dejan de coincidir, el chofer ve un número en su
 * pantalla y cobra otro en un viaje cubierto por la app.
 *
 * ═══ DE DÓNDE SALEN ESTOS NÚMEROS (27-sep-2026) ═══
 *
 * La ESTRUCTURA es la de la industria: tarifa base + costo por km + costo por
 * MINUTO + un mínimo. El minuto no estaba antes y en Santo Domingo pesa: los
 * mismos 10 km son 25 minutos a las 3 de la tarde y 45 en hora pico, y sin ese
 * término el chofer pone el tapón de su bolsillo.
 *
 * El NIVEL no se copió de nadie: las únicas cifras públicas que existen para
 * Santo Domingo son las que Uber publicó en 2018 (UberX: base 35, RD$10/km,
 * RD$4/min, mínimo 85) y tienen ocho años. Así que se calibró contra los dos
 * viajes REALES de las pruebas de septiembre de 2026:
 *   · Cancino → Av. San Vicente de Paúl, 2,6 km / 7 min  → acordado RD$200 · esta tabla da 200
 *   · Cancino → Av. JFK,                10,7 km / 29 min → acordado RD$330 · esta tabla da 340
 * El mínimo de carro es 200 por eso mismo: la fórmula sola daba 150 para el
 * viaje corto y nadie en esta ciudad maneja 2,6 km por 150.
 */
const PRICING: Record<VehicleKind, { base: number; perKm: number; perMin: number; min: number }> = {
  MOTORCYCLE: { base: 40, perKm: 11, perMin: 2, min: 120 },
  CAR:        { base: 60, perKm: 18, perMin: 3, min: 200 },
  MINIBUS:    { base: 100, perKm: 28, perMin: 4.5, min: 300 },
};

export function suggestFare(km: number, vehicle: VehicleKind = 'CAR'): number {
  const p = PRICING[vehicle];
  const raw = p.base + km * p.perKm + estimateMinutes(km, vehicle) * p.perMin;
  // Redondear a múltiplos de 10 (más cómodo para negociar)
  const rounded = Math.round(Math.max(raw, p.min) / 10) * 10;
  return rounded;
}

/**
 * Estima tiempo de viaje (minutos) asumiendo velocidad promedio urbana.
 * Simple: km / velocidad. Sin tráfico real — para eso hace falta Google Directions.
 */
export function estimateMinutes(km: number, vehicle: VehicleKind = 'CAR'): number {
  const avgSpeedKmh = vehicle === 'MOTORCYCLE' ? 28 : 22;
  return Math.max(2, Math.round((km / avgSpeedKmh) * 60));
}

/**
 * Encuentra los N drivers más cercanos a una ubicación dada.
 * `drivers` debe tener {lat, lng} válidos; los que no tengan coordenadas se descartan.
 */
export function nearestDrivers<T extends { lat?: number | null; lng?: number | null }>(
  origin: LatLng,
  drivers: T[],
  limit = 5
): (T & { distanceKm: number })[] {
  return drivers
    .filter(d => typeof d.lat === 'number' && typeof d.lng === 'number')
    .map(d => ({
      ...d,
      distanceKm: haversineKm(origin, { lat: d.lat as number, lng: d.lng as number }),
    }))
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, limit);
}

// =============================================================================
// ZONAS DE BÚSQUEDA — para que el chofer vea SOLO los viajes de su área.
// -----------------------------------------------------------------------------
// El problema que resuelve: antes el chofer recibía los viajes PENDING de TODO
// el país, recortados a los 10 más recientes. Un chofer en el Distrito Nacional
// podía tener la lista llena de solicitudes de Santiago y Puerto Plata, y no
// llegar a ver la que tenía a dos cuadras. Así se pierden viajes, choferes y
// pasajeros — es justo el motivo por el que la gente abandona estas apps.
//
// Cómo funciona: se parte el mapa en celdas de 0.2° (unos 22 km de lado en la
// latitud de República Dominicana). Cada viaje guarda la celda de su origen.
// El chofer consulta su celda MÁS las 8 vecinas, o sea un cuadrado de ~66 km
// de lado: cubre un distrito completo con holgura y mantiene el alcance largo
// que pidió el fundador. Después se filtra por distancia real y se ordena por
// cercanía, para que lo primero que vea sea lo que tiene al lado.
//
// Por qué así y no con geoconsultas de librería: es aritmética pura, sin
// dependencias ni llamadas de red, funciona con el índice que Firestore ya
// sabe hacer, y cubre el país entero sin listas de ciudades que mantener.
// =============================================================================

/** Tamaño de celda en grados. 0.2° ≈ 22 km en RD. */
const TAM_CELDA = 0.2;

/** Clave de la celda que contiene una coordenada. Ej.: "92:-350". */
export function zonaDe(lat: number, lng: number): string {
  const fy = Math.floor(lat / TAM_CELDA);
  const fx = Math.floor(lng / TAM_CELDA);
  return `${fy}:${fx}`;
}

/**
 * La celda del punto + las 8 que la rodean (9 en total).
 * Firestore permite hasta 30 valores en un `in`, así que caben de sobra.
 */
export function zonasVecinas(lat: number, lng: number): string[] {
  const fy = Math.floor(lat / TAM_CELDA);
  const fx = Math.floor(lng / TAM_CELDA);
  const claves: string[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) claves.push(`${fy + dy}:${fx + dx}`);
  }
  return claves;
}

/** Texto corto de distancia para mostrarle al chofer: "350 m", "2.4 km". */
export function distanciaTexto(km: number): string {
  if (!Number.isFinite(km)) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}
